import { ComponentType, FC, useCallback, useEffect, useMemo, useState } from 'react';

import responseSelectButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/response_select_button_click';
import { VSpacing } from '@hh.ru/magritte-ui';

import ContactFeedbackBanner from 'src/components/EmployerSearch/ContactFeedbackBanner';
import { useBannerProps } from 'src/components/EmployerSearch/ContactFeedbackBanner/hooks/useBannerProps';
import { useExperiment } from 'src/hooks/useExperiment';
import { useIsPfpVacancy } from 'src/hooks/usePfpVacancy';
import { useSelector } from 'src/hooks/useSelector';
import { useToggleState } from 'src/hooks/useToggleState';
import { EmployerStateExtName, EmployerStateType } from 'src/models/negotiationTopic.types';
import { Permission } from 'src/models/resume/resumeCommon.types';
import { ResumeCardData, TopicState } from 'src/models/resumeCard';
import { SettingType } from 'src/models/search/resume/result';
import { UserType } from 'src/models/userType';

import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import ActionsUniversal from 'src/components/ResumeCard/Actions/Universal';
import Body from 'src/components/ResumeCard/Body';
import Content from 'src/components/ResumeCard/Content';
import Header from 'src/components/ResumeCard/Header';
import Wrapper from 'src/components/ResumeCard/Wrapper';
import { useVacancyId } from 'src/components/ResumeCard/hooks/useVacancyId';

interface Props {
    ActionsComponent?: FC | ComponentType;
    chatId?: number;
    goal?: string;
    isButton?: boolean;
    isChangeState?: boolean;
    isDiscard?: boolean;
    isFirstGift?: boolean;
    isFirstResponse?: boolean;
    isGiftedResume?: boolean;
    isNew?: boolean;
    isResponse?: boolean;
    isSelected?: boolean;
    isSuitableResume?: boolean;
    isVisitor?: boolean;
    lastEmployerStateExtName?: EmployerStateExtName;
    markAsRead?: () => void;
    onSelect?: (isSelected: boolean) => void;
    resume: ResumeCardData;
    selectByClick?: boolean;
    showContacts?: boolean;
    showDeepLinks?: boolean;
    showFolders?: boolean;
    showVisited?: boolean;
    videoLink?: string;
    onResumeUpdate?: (resume: ResumeCardData) => void;
    isFirstCard?: boolean;
    isAllowedToShowFeedbackBanner?: boolean;
}

const ResumeCard: FC<Props> = ({
    ActionsComponent = ActionsUniversal,
    chatId,
    goal,
    isButton = false,
    isChangeState = false,
    isDiscard = false,
    isFirstGift = false,
    isGiftedResume = false,
    isNew = false,
    isResponse = false,
    isSelected = false,
    isSuitableResume = false,
    isVisitor = false,
    lastEmployerStateExtName,
    markAsRead,
    onSelect,
    selectByClick = true,
    showContacts = false,
    showDeepLinks = false,
    showFolders = false,
    showVisited = true,
    videoLink,
    resume,
    onResumeUpdate,
    isFirstCard = false,
    isAllowedToShowFeedbackBanner,
}) => {
    const activeSettings = useSelector((state) => state.resumeSearchResult.settings.activeSettings);
    const isEmployer = useSelector((state) => state.userType === UserType.Employer);
    const isReadOnly = useSelector((state) => state.readOnlyBanner.isReadOnly);
    const hasComments = useSelector((state) => state.commentsByUserId?.[resume._attributes.user])?.total > 0;
    const topicList = useSelector((state) => state.candidatesList?.shortTopicList?.topicList);
    const topicId = resume.topicId;
    let vacancyId = useVacancyId();
    if (topicList && topicId && !vacancyId) {
        vacancyId = topicList[Number(topicId)].vacancyId;
    }
    const isPfpVacancy = useIsPfpVacancy(vacancyId);
    const showAdditional = isEmployer && !('suitable' in resume._attributes);
    const commentToggle = useToggleState(activeSettings.includes(SettingType.CommentsExpanded) && hasComments);
    const messagesToggle = useToggleState(false);
    const [resumeState, setResumeState] = useState<TopicState | null>(resume.lastTopicState as TopicState | null);

    const isContactsOpeningExpActive = useExperiment('resume_card_contacts_opening');
    const isContactsOpened = resume._attributes.permission !== Permission.ViewWithoutContacts;
    const shouldShowContacts = (isContactsOpeningExpActive && isContactsOpened) || showContacts;
    const shouldShowDeepLinks = (isContactsOpeningExpActive && isContactsOpened) || showDeepLinks;

    const [isBannerVisible, handleContactClick] = useBannerProps({
        resumeHash: resume._attributes.hash,
        topicId,
        vacancyId,
        shouldShowBanner: resume.showContactSuccessBanner,
        isAllowedPage: isAllowedToShowFeedbackBanner,
    });

    const resumeStateUpdate = (state: EmployerStateType, updated: string) =>
        setResumeState({
            state,
            updated,
        });

    const [visited, setVisited] = useState(resume.visited);
    const [isHidden, setHidden] = useState(false);

    const markAsVisited = useCallback(() => {
        setVisited(true);
    }, []);

    const select = useCallback(() => {
        responseSelectButtonClick({ vacancyId: vacancyId?.toString() || '', topicId });
        onSelect?.(!isSelected);
    }, [onSelect, isSelected, vacancyId, topicId]);

    useEffect(() => {
        if (visited) {
            return undefined;
        }

        const handleStorageEvent = (event: StorageEvent) => {
            if (
                event.key === 'LastOpenedResume' &&
                event.newValue !== event.oldValue &&
                event.newValue === resume._attributes.hash
            ) {
                markAsVisited();
            }
        };

        window.addEventListener('storage', handleStorageEvent);
        return () => {
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, [markAsVisited, resume._attributes.hash, visited]);

    const location = useSelector((state) => state.router.location);
    useEffect(() => {
        if (resume.lastTopicState) {
            const { state, updated } = resume.lastTopicState;
            resumeStateUpdate(state, updated);
        }
    }, [location, resume.lastTopicState]);

    const isFirstResponse = isResponse && isFirstCard;

    const contextValue = useMemo(
        () => ({
            commentToggle,
            messagesToggle,
            resume,
            chatId,
            vacancyId: vacancyId?.toString() || '',
            isFirstResponse,
            isChangeState,
            isDiscard,
            resumeStateUpdate,
            isResponse,
            markAsRead,
            setHidden,
            isNew,
            isGiftedResume,
            isFirstGift,
            isReadOnly,
            showAdditional,
            isButton,
            lastEmployerStateExtName,
            isSelected,
            select,
            isFoldersPage: showFolders && selectByClick,
            isSuitableResume,
            isPfpVacancy,
            onResumeUpdate,
            isFirstCard,
            handleContactClick,
        }),
        [
            commentToggle,
            messagesToggle,
            resume,
            chatId,
            vacancyId,
            isFirstResponse,
            isChangeState,
            isDiscard,
            isResponse,
            markAsRead,
            isNew,
            isGiftedResume,
            isFirstGift,
            isReadOnly,
            showAdditional,
            isButton,
            lastEmployerStateExtName,
            isSelected,
            select,
            showFolders,
            selectByClick,
            isSuitableResume,
            isPfpVacancy,
            onResumeUpdate,
            isFirstCard,
            handleContactClick,
        ]
    );

    return (
        <>
            <Wrapper
                isClickSelectionEnabled={selectByClick}
                isHidden={isHidden}
                isNew={isNew}
                isSelected={isSelected}
                onSelect={onSelect}
                resumeId={resume._attributes.id}
                resumeHash={resume._attributes.hash}
                resumeTopicId={resume.topicId}
                searchRid={resume.searchRid}
            >
                <Content isVisited={showVisited && !!visited}>
                    <Header
                        isButton={isButton}
                        isFirstGift={isFirstGift}
                        isFirstResponse={isFirstResponse}
                        isFolders={showFolders}
                        isGifted={isGiftedResume}
                        isResponse={isResponse}
                        isVisited={showVisited && !!visited}
                        isVisitor={isVisitor}
                        markAsVisited={markAsVisited}
                        markAsRead={markAsRead}
                        resume={resume}
                        topicState={resumeState}
                        videoLink={videoLink}
                        vacancyId={vacancyId}
                        isPfpVacancy={isPfpVacancy}
                    />
                    <VSpacing default={12} />
                    <Body
                        resume={resume}
                        isGifted={isGiftedResume}
                        isNew={isNew}
                        markAsRead={markAsRead}
                        isResponse={isResponse}
                        goal={goal}
                        vacancyId={vacancyId?.toString() || ''}
                        showContacts={shouldShowContacts}
                        showDeepLinks={shouldShowDeepLinks}
                        showVisited={showVisited}
                        visited={!!visited}
                        chatId={chatId}
                        onContactsClick={handleContactClick}
                    />
                </Content>
                {isBannerVisible && (
                    <ContactFeedbackBanner
                        paddingSize="small"
                        resumeHash={resume._attributes.hash}
                        vacancyId={vacancyId}
                        topicId={topicId}
                    />
                )}
                <ResumeCardActionsContext.Provider value={contextValue}>
                    <ActionsComponent />
                </ResumeCardActionsContext.Provider>
            </Wrapper>
            {!isHidden && <VSpacing default={16} />}
        </>
    );
};

export default ResumeCard;
